import { IDENTITY_POLICY } from "../policy";
import { invitationStatus, requestStatus, seatUsage } from "../logic/invitations";
import { absoluteExpiresAt, idleExpiresAt } from "../logic/session-clock";
import type {
  Invitation,
  Member,
  OrganisationDetails,
  OrgAccessRequest,
  ProjectAccessRequest,
  Session,
  StaffOrganisation,
} from "../types";
import { findOrg, permissionsFor, planOf, roleIn, type Ctx } from "./context";
import type {
  StoredInvitation,
  StoredOrganisation,
  StoredOrgRequest,
  StoredProjectRequest,
  StoredSession,
  StoredUser,
} from "./state";

/** Turns stored records into what the API returns: ISO dates, computed states, no hashes. */

const iso = (ms: number) => new Date(ms).toISOString();

export function sessionView(ctx: Ctx, session: StoredSession, user: StoredUser): Session {
  const org =
    session.organisationId === undefined ? undefined : findOrg(ctx, session.organisationId);
  const role = roleIn(ctx, user.id, org?.id);
  const memberships = ctx.state.memberships
    .filter((m) => m.userId === user.id)
    .flatMap((m) => {
      const membershipOrg = findOrg(ctx, m.organisationId);
      return membershipOrg === undefined
        ? []
        : [
            {
              organisationId: m.organisationId,
              organisationName: membershipOrg.name,
              role: m.role,
            },
          ];
    });
  return {
    id: session.id,
    user: {
      id: user.id,
      name: user.name,
      email: user.email,
      initials: user.initials,
      colour: user.colour,
    },
    organisation:
      org === undefined || role === undefined
        ? undefined
        : {
            id: org.id,
            name: org.name,
            role,
            setupComplete: org.setupComplete,
            activated: planOf(org) !== null,
            plan: planOf(org),
          },
    memberships,
    permissions: permissionsFor(role, org?.policies?.projectCreation),
    authMethod: session.authMethod,
    authenticatedAt: iso(session.createdAt),
    idleExpiresAt: iso(idleExpiresAt(session, ctx.options.limits)),
    absoluteExpiresAt: iso(absoluteExpiresAt(session, ctx.options.limits)),
    terms: { current: IDENTITY_POLICY.termsVersion, accepted: user.termsVersion },
    workspaceChosen: user.workspaceChosen,
  };
}

export function pendingInvitations(ctx: Ctx, organisationId: string): StoredInvitation[] {
  const now = ctx.now();
  return ctx.state.invitations.filter(
    (inv) => inv.organisationId === organisationId && invitationStatus(inv, now) === "pending",
  );
}

export function orgUsage(ctx: Ctx, organisationId: string) {
  const members = ctx.state.memberships
    .filter((m) => m.organisationId === organisationId)
    .map((m) => m.role);
  return seatUsage(
    members,
    pendingInvitations(ctx, organisationId).map((inv) => inv.role),
  );
}

export function regionLabel(regionId: string): string {
  return regionId === IDENTITY_POLICY.storageRegion.id
    ? IDENTITY_POLICY.storageRegion.label
    : regionId;
}

export function orgDetails(ctx: Ctx, org: StoredOrganisation): OrganisationDetails {
  const usage = orgUsage(ctx, org.id);
  const words = org.name.trim().split(/\s+/);
  return {
    id: org.id,
    name: org.name,
    logoInitials: words
      .slice(0, 2)
      .map((word) => word[0] ?? "")
      .join("")
      .toUpperCase(),
    country: org.country,
    state: org.state,
    city: org.city,
    region: regionLabel(org.region),
    status: org.status,
    allowances: { ...org.allowances },
    usage: { seats: usage.seats, guests: usage.guests, storageGb: org.storageUsedGb },
    setupComplete: org.setupComplete,
    accessUntil: org.accessUntil,
    projectPrefix: org.projectPrefix,
  };
}

export function memberView(
  user: StoredUser,
  role: Member["role"],
  joinedAt: number,
  you: boolean,
  suspended = false,
): Member {
  return {
    userId: user.id,
    name: user.name,
    email: user.email,
    initials: user.initials,
    colour: user.colour,
    role,
    joinedAt: iso(joinedAt),
    you,
    status: suspended ? "suspended" : "active",
  };
}

export function invitationView(ctx: Ctx, invitation: StoredInvitation): Invitation {
  return {
    id: invitation.id,
    email: invitation.email,
    role: invitation.role,
    status: invitationStatus(invitation, ctx.now()),
    invitedBy: invitation.invitedBy,
    createdAt: iso(invitation.createdAt),
    expiresAt: iso(invitation.expiresAt),
    lastSentAt: iso(invitation.lastSentAt),
  };
}

export function orgRequestView(request: StoredOrgRequest): OrgAccessRequest {
  return {
    id: request.id,
    name: request.name,
    email: request.email,
    firm: request.firm,
    city: request.city,
    teamSize: request.teamSize,
    disciplines: [...request.disciplines],
    message: request.message,
    status: request.status,
    createdAt: iso(request.createdAt),
    decidedAt: request.decidedAt === undefined ? undefined : iso(request.decidedAt),
    decisionMessage: request.decisionMessage,
    organisationId: request.organisationId,
  };
}

export function projectRequestView(ctx: Ctx, request: StoredProjectRequest): ProjectAccessRequest {
  const user = ctx.state.users.find((u) => u.id === request.userId);
  return {
    id: request.id,
    projectId: request.projectId,
    projectName: request.projectName,
    requester: {
      id: request.userId,
      name: user?.name ?? "Former member",
      email: user?.email ?? "",
      initials: user?.initials ?? "?",
      colour: user?.colour ?? 1,
    },
    message: request.message,
    status: requestStatus(request, ctx.now()),
    createdAt: iso(request.createdAt),
    expiresAt: iso(request.expiresAt),
    role: request.role,
  };
}

export function staffOrgView(ctx: Ctx, org: StoredOrganisation): StaffOrganisation {
  const usage = orgUsage(ctx, org.id);
  const owners = ctx.state.memberships
    .filter((m) => m.organisationId === org.id && m.role === "owner")
    .map((m) => ctx.state.users.find((u) => u.id === m.userId)?.email ?? "")
    .filter((email) => email !== "");
  const invitedOwners = pendingInvitations(ctx, org.id)
    .filter((inv) => inv.role === "owner")
    .map((inv) => `${inv.email} (invited)`);
  return {
    id: org.id,
    workosOrganisationId: org.workosId,
    name: org.name,
    projectPrefix: org.projectPrefix,
    country: org.country,
    state: org.state,
    city: org.city,
    region: regionLabel(org.region),
    status: org.status,
    allowances: { ...org.allowances },
    usage,
    accessFrom: org.accessFrom,
    accessUntil: org.accessUntil,
    owners: [...owners, ...invitedOwners],
    plan: planOf(org),
    setupComplete: org.setupComplete,
    createdAt: iso(org.createdAt),
  };
}
